import { get, post } from 'aws-amplify/api';
import { ImageVariant } from '../API';

/** Returns a promise for an Axios response, not a Fetch response */
const getImageUploadUrlApiCall = (imageObjectId: string, fileName: string) => {
  return get({
    apiName: 'assetapi',
    path: '/images/uploads',
    options: {
      queryParams: {
        mealId: imageObjectId,
        fileName,
      },
    },
  }).response;
};

const uploadToS3ApiCall = (imageUploadUrl: string, blob: File) => {
  return fetch(imageUploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    body: blob,
  });
};

/** Returns a promise for an Axios response, not a Fetch response */
const processImageApiCall = (
  imageObjectId: string,
  fileName: string,
  width?: number | null | undefined,
  height?: number | null | undefined,
  sizeDescriptor?: string | null | undefined,
) => {
  const queryStringParameters: Record<string, string> = {
    mealId: imageObjectId,
    fileName,
  };
  if (width !== null && width !== undefined) {
    queryStringParameters.width = width.toString();
  }
  if (height !== null && height !== undefined) {
    queryStringParameters.height = height.toString();
  }
  if (sizeDescriptor !== null && sizeDescriptor !== undefined) {
    queryStringParameters.sizeDescriptor = sizeDescriptor.toString();
  }
  return post({
    apiName: 'assetapi',
    path: '/images',
    options: {
      queryParams: queryStringParameters,
    },
  }).response;
};

export const uploadUserImage = async (
  imageObjectId: string,
  fileList: FileList,
  returnOriginal?: boolean,
): Promise<string | undefined> => {
  if (!fileList.length) {
    console.log('No files');
    return undefined;
  }
  const file = fileList[0];

  const getUploadUrlResponse = await getImageUploadUrlApiCall(
    imageObjectId,
    file.name,
  );
  const { statusCode: getUploadUrlStatus, body: getUploadUrlBody } =
    getUploadUrlResponse;
  if (getUploadUrlStatus < 200 || getUploadUrlStatus > 399) {
    console.log('getImageUploadUrl failed');
    console.log({ getUploadUrlResponse });
    return undefined;
  }
  const { path: originalUrl, uploadUrl } =
    (await getUploadUrlBody.json()) as any;
  const uploadResponse = await uploadToS3ApiCall(uploadUrl, file);
  if (!uploadResponse.ok) {
    console.log('Upload to S3 failed');
    console.log({ uploadResponse });
    return undefined;
  }

  if (returnOriginal) {
    return originalUrl;
  }

  const processResponse = await processImageApiCall(imageObjectId, file.name);
  const { statusCode: processImageStatus, body: processImageBody } =
    processResponse;
  if (processImageStatus < 200 || processImageStatus > 399) {
    console.log('processImage failed');
    console.log({ processResponse });
  }

  const { path: newImageUrl } = (await processImageBody.json()) as any;
  return newImageUrl;
};

const parseDimensions = (
  sizeDescriptor: string,
): { width: number; height: number } | null => {
  const stringDimensions = sizeDescriptor.split('x');
  if (stringDimensions.length !== 2) {
    return null;
  }
  const width = parseInt(stringDimensions[0], 10);
  if (width <= 0) {
    return null;
  }
  const height = parseInt(stringDimensions[1], 10);
  if (height <= 0) {
    return null;
  }
  return { width, height };
};

const processImageSize = async (
  imageObjectId: string,
  fileName: string,
  sizeDescriptor: string,
): Promise<ImageVariant> => {
  const dimensions = parseDimensions(sizeDescriptor);
  const width = dimensions && dimensions.width;
  const height = dimensions && dimensions.height;

  const processResponse = await processImageApiCall(
    imageObjectId,
    fileName,
    width,
    height,
    sizeDescriptor,
  );
  const { statusCode: processImageStatus, body: processImageBody } =
    processResponse;
  if (processImageStatus < 200 || processImageStatus > 399) {
    console.log('processImage failed');
    console.log({ processResponse });
  }

  const { path: newImageUrl } = (await processImageBody.json()) as any;
  return {
    sizeDescriptor,
    imageUrl: newImageUrl,
  } as ImageVariant;
};

export const VARIANT_SIZE_DESCRIPTOR_ORIGINAL = 'original';

export const uploadUserImageVariants = async (
  imageObjectId: string,
  fileList: FileList,
  variantSizeDescriptors: Array<string>,
): Promise<ImageVariant[] | undefined> => {
  if (!fileList.length) {
    console.log('No files');
    return undefined;
  }
  const file = fileList[0];

  const getUploadUrlResponse = await getImageUploadUrlApiCall(
    imageObjectId,
    file.name,
  );
  const { statusCode: getUploadUrlStatus, body: getUploadUrlBody } =
    getUploadUrlResponse;
  if (getUploadUrlStatus < 200 || getUploadUrlStatus > 399) {
    console.log('getImageUploadUrl failed');
    console.log({ getUploadUrlResponse });
    return undefined;
  }
  const { path: originalUrl, uploadUrl } =
    (await getUploadUrlBody.json()) as any;
  const uploadResponse = await uploadToS3ApiCall(uploadUrl, file);
  if (!uploadResponse.ok) {
    console.log('Upload to S3 failed');
    console.log({ uploadResponse });
    return undefined;
  }

  const resizedVariants = await Promise.all(
    variantSizeDescriptors.map((sizeDescriptor) =>
      processImageSize(imageObjectId, file.name, sizeDescriptor),
    ),
  );

  return [
    {
      sizeDescriptor: VARIANT_SIZE_DESCRIPTOR_ORIGINAL,
      imageUrl: originalUrl,
    } as ImageVariant,
    ...resizedVariants,
  ];
};
